/**
 * Bug Helper Functions
 * Utility functions for bug-related operations
 */

import { STATUS_COLORS } from '@/constants/statusColors'
import {
  BUG_STATUS,
  BUG_PRIORITY,
  BUG_SEVERITY,
  BUG_TYPE,
  ENVIRONMENT,
  BROWSER,
  OS,
  BUG_STATUS_OPTIONS,
  BUG_PRIORITY_OPTIONS,
  BUG_SEVERITY_OPTIONS,
  BUG_TYPE_OPTIONS,
  BROWSER_OPTIONS,
  OS_OPTIONS
} from '@/constants/bugs'

/**
 * Get badge color for bug status
 * @param {string} status - Bug status value
 * @returns {string} Color name for Badge component
 */
export const getBugStatusColor = (status) => {
  const statusColors = {
    [BUG_STATUS.OPEN]: 'red',        // Red - needs attention
    [BUG_STATUS.IN_PROGRESS]: 'blue', // Blue - being worked on
    [BUG_STATUS.RESOLVED]: 'green',   // Green - fixed
    [BUG_STATUS.CLOSED]: 'gray',      // Gray - completed/archived
    [BUG_STATUS.REOPENED]: 'orange',  // Orange - needs re-attention
    [BUG_STATUS.BLOCKED]: 'purple',   // Purple - blocked/waiting
    [BUG_STATUS.NOT_A_BUG]: 'yellow', // Yellow - not a bug
    [BUG_STATUS.SETUP_ISSUE]: 'cyan'  // Cyan - setup/config issue
  }
  
  return statusColors[status] || 'gray'
}

/**
 * Get badge color for bug priority
 * @param {string} priority - Bug priority value
 * @returns {string} Color name for Badge component
 */
export const getBugPriorityColor = (priority) => {
  const priorityColors = {
    [BUG_PRIORITY.CRITICAL]: 'red',
    [BUG_PRIORITY.HIGH]: 'orange',
    [BUG_PRIORITY.MEDIUM]: 'yellow',
    [BUG_PRIORITY.LOW]: 'green'
  }
  
  return priorityColors[priority] || 'gray'
}

/**
 * Get badge color for bug severity
 * @param {string} severity - Bug severity value
 * @returns {string} Color name for Badge component
 */
export const getBugSeverityColor = (severity) => {
  const severityColors = {
    [BUG_SEVERITY.CRITICAL]: 'red',
    [BUG_SEVERITY.HIGH]: 'orange',
    [BUG_SEVERITY.MEDIUM]: 'yellow',
    [BUG_SEVERITY.LOW]: 'green'
  }
  
  return severityColors[severity] || 'gray'
}

/**
 * Format bug status for display
 * @param {string} status - Bug status value
 * @returns {string} Formatted status label
 */
export const formatBugStatus = (status) => {
  const option = BUG_STATUS_OPTIONS.find(opt => opt.value === status)
  return option?.label || status
}

/**
 * Format bug priority for display
 * @param {string} priority - Bug priority value
 * @returns {string} Formatted priority label
 */
export const formatBugPriority = (priority) => {
  const option = BUG_PRIORITY_OPTIONS.find(opt => opt.value === priority)
  return option?.label || priority
}

/**
 * Format bug severity for display
 * @param {string} severity - Bug severity value
 * @returns {string} Formatted severity label
 */
export const formatBugSeverity = (severity) => {
  const option = BUG_SEVERITY_OPTIONS.find(opt => opt.value === severity)
  return option?.label || severity
}

/**
 * Get bug type label
 * @param {string} type - Bug type value
 * @returns {string} Bug type label
 */
export const getBugTypeLabel = (type) => {
  const option = BUG_TYPE_OPTIONS.find(opt => opt.value === type)
  return option?.label || type
}

/**
 * Get badge color for environment
 * @param {string} environment - Environment value
 * @returns {string} Color name for Badge component
 */
export const getBugEnvironmentColor = (environment) => {
  const envColors = {
    [ENVIRONMENT.DEVELOPMENT]: 'gray',
    [ENVIRONMENT.VERSIONED_STAGING]: 'blue',
    [ENVIRONMENT.DEFAULT_STAGING]: 'blue',
    [ENVIRONMENT.INTEROP]: 'purple',
    [ENVIRONMENT.BETA]: 'orange',
    [ENVIRONMENT.PRODUCTION]: 'red'
  }
  
  return envColors[environment] || 'gray'
}

/**
 * Get browser label
 * @param {string} browser - Browser value
 * @returns {string} Browser label
 */
export const getBrowserLabel = (browser) => {
  const option = BROWSER_OPTIONS.find(opt => opt.value === browser)
  return option?.label || browser
}

/**
 * Get OS label
 * @param {string} os - OS value
 * @returns {string} OS label
 */
export const getOSLabel = (os) => {
  const option = OS_OPTIONS.find(opt => opt.value === os)
  return option?.label || os
}

/**
 * Check if bug is high priority (critical or high)
 * @param {string} priority - Bug priority value
 * @returns {boolean} True if high priority
 */
export const isHighPriority = (priority) => {
  return priority === BUG_PRIORITY.CRITICAL || priority === BUG_PRIORITY.HIGH
}

/**
 * Check if bug is open or in progress
 * @param {string} status - Bug status value
 * @returns {boolean} True if bug is active
 */
export const isActiveBug = (status) => {
  return status === BUG_STATUS.OPEN || 
         status === BUG_STATUS.IN_PROGRESS || 
         status === BUG_STATUS.REOPENED || 
         status === BUG_STATUS.SETUP_ISSUE
}

/**
 * Check if bug is resolved or closed
 * @param {string} status - Bug status value
 * @returns {boolean} True if bug is resolved
 */
export const isResolvedBug = (status) => {
  return status === BUG_STATUS.RESOLVED || 
         status === BUG_STATUS.CLOSED || 
         status === BUG_STATUS.NOT_A_BUG
}

